import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { ToastContainer, type ToastItem, type ToastTone } from '@/components/ui/ToastContainer'

interface ToastInput {
  title: string
  message?: string
  tone?: ToastTone
  durationMs?: number
}

interface ToastContextValue {
  pushToast: (toast: ToastInput) => string
  dismissToast: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

let toastSeq = 0

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const pushToast = useCallback(
    (toast: ToastInput) => {
      const id = `toast_${Date.now()}_${toastSeq++}`
      const item: ToastItem = {
        id,
        title: toast.title,
        message: toast.message,
        tone: toast.tone ?? 'info',
        durationMs: toast.durationMs ?? 5000,
      }
      setToasts((prev) => [...prev.slice(-4), item])
      return id
    },
    [],
  )

  const value = useMemo(
    () => ({ pushToast, dismissToast }),
    [pushToast, dismissToast],
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider')
  }
  return ctx
}
